// import { Device, DeviceInfo } from '@capacitor/device';
import {
  useAppleExternalSignIn,
  useFacebookExternalSignIn,
  useGoogleExternalSignIn,
  useLogin,
} from 'app/core/api/auth/api-auth';
import {
  appleSignIn,
  facebookSignIn,
  googleSignIn,
  initializeFacebookSignIn,
} from 'app/core/api/auth/external-sign-in';
import { appEnv } from 'app/core/environment';
import { useAuthToken } from 'app/core/token/token-utils';
import AppImage from 'app/shared/components/app-image/AppImage';
import Button from 'app/shared/components/button/Button';
import IconLib, {
  AppleLogoSvg,
  FacebookLogoSvg,
  GoogleLogoSvg,
} from 'app/shared/components/icon/icon-lib';
import {
  ErrorText,
  InsetAreaSpacer,
  PadCollapse,
  PadExpand,
  TopBannerText,
} from 'app/shared/components/misc';
import Body from 'app/shared/components/typography/body/Body';
import Form from 'app/shared/frames/Form';
import SharedRoutePath from 'app/shared/SharedRoutePath';
// import { useEffectOnce } from 'app/tools/hooks/misc-hooks';
import logoBanner from 'assets/logo-banner.svg';
import React from 'react';
import { Link, Redirect } from 'react-router-dom';
import { from, switchMap } from 'rxjs';
import styled from 'styled-components/macro';
import styledFuncs from 'app/styles/styledFuncs';
import { tap } from 'rxjs/operators';
import CardIconButton from '../components/card/CardIconButton';
import FormTextInput from '../components/form-input/FormTextInput';
import useInputForm from '../components/form-input/utilities/use-input-form';
import Subheading from '../components/typography/subheading/Subheading';

const RedLink = styled(Link)`
  color: #f6645b;
  position: relative;
  font-size: 10px;
  font-weight: 400;
  right: ${styledFuncs.pad(-18.5)};
  margin-bottom: ${styledFuncs.pad(0.3)};
`;

type FormValues = {
  username: string;
  password: string;
};

export default function Login(): JSX.Element {
  const auth = useAuthToken();
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  initializeFacebookSignIn();
  // const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>();
  // useEffectOnce(() => {
  //   // eslint-disable-next-line @typescript-eslint/no-floating-promises
  //   Device.getInfo().then(devicedetail => {
  //     setDeviceInfo(devicedetail);
  //   });
  // });

  const {
    request$: login$,
    sending: loginWaiting,
    error: loginError,
  } = useLogin();

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { handleSubmit, setValue, inputProps } =
    useInputForm<FormValues>({
      mode: 'onTouched',
      apiError: loginError,
    });

  const submit$ = (data: FormValues) =>
    login$(data).pipe(
      tap({
        error: () => setValue('password', ''),
      })
    );
  const {
    request$: externalAppleSignIn$,
    sending: loginWaiting2,
    error: loginError2,
  } = useAppleExternalSignIn();

  const {
    request$: externalFacebookSignIn$,
    sending: loginWaiting3,
    error: loginError3,
  } = useFacebookExternalSignIn();

  const {
    request$: externalGoogleSignIn$,
    sending: loginWaiting4,
    error: loginError4,
  } = useGoogleExternalSignIn();

  const submitting =
    loginWaiting || loginWaiting2 || loginWaiting3 || loginWaiting4;
  const loginApiError =
    loginError || loginError2 || loginError3 || loginError4;
  if (auth) return <Redirect to={SharedRoutePath.HOME} />;

  return (
    <>
      {appEnv.APP_VARIANT === 'business' ? (
        <TopBannerText>Business App</TopBannerText>
      ) : (
        <InsetAreaSpacer pos="top" />
      )}
      <PadCollapse pad={appEnv.APP_VARIANT === 'business' ? 5 : 7} />
      <AppImage height="4rem" src={logoBanner} alt="logo banner" />
      <PadCollapse pad={2} />
      <Subheading color="primary">Let's get started!</Subheading>
      <Form margin onSubmit={handleSubmit(submit$)}>
        <FormTextInput
          controlName="username"
          placeholder="Email"
          autoComplete="email"
          type="email"
          icon={IconLib.MAIL}
          classicPlaceholder
          {...inputProps}
        />
        <FormTextInput
          controlName="password"
          type="password"
          icon={IconLib.LOCK}
          classicPlaceholder
          {...inputProps}
        />
        <ErrorText message={loginApiError} />
        <Body tiny centered={false}>
          <RedLink to={SharedRoutePath.RESET_PASSWORD}>
            Forgot password?
          </RedLink>
        </Body>
        <PadCollapse pad={1} />
        <Button type="submit" pending={submitting}>
          Sign in
        </Button>
        <Body small centered>
          Not registered?{' '}
          <Link to={SharedRoutePath.SIGN_UP}>Create an account</Link>
        </Body>
      </Form>
      <div>
        <Body small centered>
          Or sign in with:
        </Body>
        <PadCollapse pad={2} />
        <CardIconButton
          imgNode={<AppleLogoSvg />}
          alt="apple"
          color="black"
          onClick={() => {
            from(appleSignIn())
              .pipe(switchMap(externalAppleSignIn$))
              .subscribe();
          }}
        />
        {/* {(deviceInfo?.operatingSystem === 'ios' ||
          deviceInfo?.operatingSystem === 'mac') && (
          <CardIconButton
            imgNode={<AppleLogoSvg />}
            alt="apple"
            color="black"
            onClick={() => {
              from(appleSignIn())
                .pipe(switchMap(externalAppleSignIn$))
                .subscribe();
            }}
          />
        )} */}
        <CardIconButton
          imgNode={<FacebookLogoSvg />}
          alt="apple"
          color="#1877F2"
          onClick={() => {
            from(facebookSignIn())
              .pipe(switchMap(externalFacebookSignIn$))
              .subscribe();
          }}
        />
        <CardIconButton
          imgNode={<GoogleLogoSvg />}
          alt="apple"
          color="#FFFFFF"
          onClick={() => {
            from(googleSignIn())
              .pipe(switchMap(externalGoogleSignIn$))
              .subscribe();
          }}
        />
      </div>
      <PadCollapse pad={2} />
      <PadExpand />
      <div>
        <Body tiny centered>
          By signing in, you agree to our
          <br />
          <Link to={SharedRoutePath.TERMS_Of_USE}>
            Terms of Use
          </Link>{' '}
          and{' '}
          <Link to={SharedRoutePath.PRIVACY_POLICY}>
            Privacy Policy
          </Link>
          .
        </Body>
      </div>
      <PadCollapse pad={2} />
      <PadExpand />
      <Body tiny centered={false}>
        <Link to={SharedRoutePath.RESET_PASSWORD}>
          Forgot password?
        </Link>
      </Body>
      <PadCollapse pad={1} />
      <InsetAreaSpacer pos="bottom" />
    </>
  );
}
